import { privateKeyToAccount } from 'viem/accounts'
import { createPublicClient, http, type Account, type Chain } from 'viem'
import { base, baseSepolia } from 'viem/chains'
import { generatePrivateKey, encryptWithPRF, decryptWithPRF } from './encryption'
import type { EncryptedData } from './encryption'

export interface WalletInfo {
  address: string
  privateKey: string
  account: Account
}

export interface EncryptedWalletData {
  encryptedData: string
  iv: string
  salt: string
  address: string
  credentialId: string
}

/**
 * Get the Base chain based on environment variable
 */
export function getBaseChain(): Chain {
  // Check if we're in mainnet mode (default to testnet for safety)
  const isMainnet = import.meta.env.VITE_BASE_NETWORK === 'base'
  return isMainnet ? base : baseSepolia
}

/**
 * Get the chain name for display
 */
export function getChainName(): string {
  const chain = getBaseChain()
  return chain.name
}

/**
 * Get the RPC URL for the current Base network
 */
export function getBaseRpcUrl(): string {
  const isMainnet = import.meta.env.VITE_BASE_NETWORK === 'base'
  return isMainnet
    ? import.meta.env.VITE_BASE_MAINNET_RPC || 'https://mainnet.base.org'
    : import.meta.env.VITE_BASE_SEPOLIA_RPC || 'https://sepolia.base.org'
}

/**
 * Create a public client for Base network
 */
export function createBaseClient() {
  const chain = getBaseChain()
  const rpcUrl = getBaseRpcUrl()
  return createPublicClient({
    chain,
    transport: http(rpcUrl),
  })
}

/**
 * Create a new wallet with a random private key
 */
export function createWallet(): WalletInfo {
  const privateKey = generatePrivateKey() as `0x${string}`
  const account = privateKeyToAccount(privateKey)

  return {
    address: account.address,
    privateKey,
    account,
  }
}

/**
 * Import a wallet from an existing private key
 */
export function importWallet(privateKey: string): WalletInfo {
  // Ensure private key has 0x prefix
  const formattedKey = privateKey.startsWith('0x')
    ? (privateKey as `0x${string}`)
    : (`0x${privateKey}` as `0x${string}`)

  try {
    const account = privateKeyToAccount(formattedKey)

    return {
      address: account.address,
      privateKey: formattedKey,
      account,
    }
  } catch (error) {
    console.error('Error importing wallet:', error)
    throw new Error('Invalid private key. Please check the format.')
  }
}

/**
 * Encrypt a wallet's private key using PRF-derived key from WebAuthn
 * SECURITY: Only accepts PRF keys - no fallback to credential ID
 */
export async function encryptWallet(
  wallet: WalletInfo,
  prfKey: ArrayBuffer,
  credentialId: string
): Promise<EncryptedWalletData> {
  const encrypted = await encryptWithPRF(wallet.privateKey, prfKey)

  return {
    ...encrypted,
    address: wallet.address,
    credentialId,
  }
}

/**
 * Decrypt a wallet's private key using PRF-derived key from WebAuthn
 * SECURITY: Only accepts PRF keys - no fallback to credential ID
 */
export async function decryptWallet(
  encryptedWallet: EncryptedWalletData,
  prfKey: ArrayBuffer
): Promise<WalletInfo> {
  const encryptedData: EncryptedData = {
    encryptedData: encryptedWallet.encryptedData,
    iv: encryptedWallet.iv,
    salt: encryptedWallet.salt,
  }

  const privateKey = await decryptWithPRF(encryptedData, prfKey)
  return importWallet(privateKey)
}

/**
 * Get USDC balance for an address on Base
 * Note: This requires the USDC contract address
 */
export async function getUSDCBalance(address: string): Promise<string> {
  const chain = getBaseChain()
  const client = createBaseClient()

  // USDC contract addresses on Base
  const USDC_ADDRESS =
    chain.id === base.id
      ? '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' // Base Mainnet USDC
      : '0x036CbD53842c5426634e7929541eC2318f3dCF7e' // Base Sepolia USDC

  try {
    // ERC20 balanceOf function signature
    const data = await client.readContract({
      address: USDC_ADDRESS as `0x${string}`,
      abi: [
        {
          name: 'balanceOf',
          type: 'function',
          stateMutability: 'view',
          inputs: [{ name: 'account', type: 'address' }],
          outputs: [{ name: 'balance', type: 'uint256' }],
        },
      ],
      functionName: 'balanceOf',
      args: [address as `0x${string}`],
    })

    // USDC has 6 decimals
    const balance = Number(data) / 1_000_000
    return balance.toFixed(2)
  } catch (error) {
    console.error('Error fetching USDC balance:', error)
    return '0.00'
  }
}

/**
 * Get ETH balance for an address
 */
export async function getETHBalance(address: string): Promise<string> {
  const client = createBaseClient()

  try {
    const balance = await client.getBalance({
      address: address as `0x${string}`,
    })

    // Convert from wei to ETH (18 decimals)
    const ethBalance = Number(balance) / 1e18
    return ethBalance.toFixed(6)
  } catch (error) {
    console.error('Error fetching ETH balance:', error)
    return '0.000000'
  }
}

/**
 * Format an Ethereum address for display (0x1234...5678)
 */
export function formatAddress(address: string): string {
  if (!address || address.length < 10) return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

/**
 * Validate Ethereum private key format
 */
export function isValidPrivateKey(privateKey: string): boolean {
  // Remove 0x prefix if present
  const key = privateKey.startsWith('0x') ? privateKey.slice(2) : privateKey

  // Check if it's 64 hex characters (32 bytes)
  return /^[a-fA-F0-9]{64}$/.test(key)
}
